import {
  compareMoney,
  type CreateDebtRequest,
  createDebtRequestSchema,
  type DebtPaymentRequest,
  debtPaymentRequestSchema,
  parseRateInput,
} from '@familywallet/shared';
import { z } from 'zod';
import { parsePositive } from './add-form';

/** What the New debt form holds: everything as typed, parsed only when saving. */
export interface DebtFormValues {
  direction: 'borrowed' | 'lent';
  ownerMemberId: string;
  /** The other side: an outside person (by id, or a new name) or a family member. */
  personId: string;
  personName: string;
  memberId: string;
  amount: string;
  currency: string;
  existing: boolean;
  accountId: string;
  counterpartAccountId: string;
  occurredOn: string;
  note: string;
  isPrivate: boolean;
  rate: string;
}

interface Issue {
  path: (string | number)[];
  message: string;
}

function amountIssue(raw: string, currency: string): string {
  if (raw.trim() === '') return 'add.errors.amountRequired';
  return currency === 'LBP' && parsePositive(raw, 'USD')
    ? 'add.errors.amountWhole'
    : 'add.errors.amount';
}

export function checkDebtForm(v: DebtFormValues, ctx: { askRate: boolean }): Issue[] {
  const issues: Issue[] = [];
  if (!parsePositive(v.amount, v.currency)) {
    issues.push({ path: ['amount'], message: amountIssue(v.amount, v.currency) });
  }
  if (!v.personId && !v.personName.trim() && !v.memberId) {
    issues.push({ path: ['personId'], message: 'debts.errors.person' });
  }
  if (!v.occurredOn) issues.push({ path: ['occurredOn'], message: 'add.errors.date' });
  if (!v.existing) {
    if (!v.accountId) issues.push({ path: ['accountId'], message: 'debts.errors.account' });
    if (v.memberId && !v.counterpartAccountId) {
      issues.push({ path: ['counterpartAccountId'], message: 'debts.errors.theirAccount' });
    }
    if (v.memberId && v.accountId && v.accountId === v.counterpartAccountId) {
      issues.push({ path: ['counterpartAccountId'], message: 'add.errors.sameAccount' });
    }
  }
  if (ctx.askRate && !parseRateInput(v.rate)) {
    issues.push({ path: ['rate'], message: 'add.errors.rate' });
  }
  return issues;
}

/** The request for POST /debts, checked by the shared schema the API uses. */
export function toDebtRequest(v: DebtFormValues, ctx: { askRate: boolean }): CreateDebtRequest {
  const request: CreateDebtRequest = {
    direction: v.direction,
    ownerMemberId: v.ownerMemberId,
    ...(v.memberId
      ? { memberId: v.memberId }
      : v.personId
        ? { personId: v.personId }
        : { personName: v.personName.trim() }),
    currency: v.currency,
    amount: parsePositive(v.amount, v.currency)!,
    occurredOn: v.occurredOn,
    existing: v.existing,
    ...(v.existing
      ? { isPrivate: v.isPrivate }
      : {
          accountId: v.accountId,
          ...(v.memberId ? { counterpartAccountId: v.counterpartAccountId } : {}),
        }),
    ...(v.note.trim() ? { note: v.note.trim() } : {}),
    ...(ctx.askRate ? { rate: parseRateInput(v.rate)! } : {}),
  };
  return createDebtRequestSchema.parse(request) as CreateDebtRequest;
}

export function debtFormSchema(ctx: { askRate: boolean }) {
  return z
    .custom<DebtFormValues>()
    .superRefine((v, c) => {
      for (const issue of checkDebtForm(v, ctx)) {
        c.addIssue({ code: 'custom', path: issue.path, message: issue.message });
      }
    })
    .transform((v) => toDebtRequest(v, ctx));
}

/** The payment form: repay or collect. */
export interface PaymentFormValues {
  amount: string;
  interest: string;
  accountId: string;
  counterpartAccountId: string;
  occurredOn: string;
  note: string;
  rate: string;
  /** A credit item can be paid in another family currency (empty: the debt's own). */
  paidCurrency: string;
}

export interface PaymentContext {
  currency: string;
  outstanding: string;
  internal: boolean;
  allowInterest: boolean;
  askRate: boolean;
}

export function checkPaymentForm(v: PaymentFormValues, ctx: PaymentContext): Issue[] {
  const issues: Issue[] = [];
  const amount = parsePositive(v.amount, ctx.currency);
  if (!amount) issues.push({ path: ['amount'], message: amountIssue(v.amount, ctx.currency) });
  else if (compareMoney(amount, ctx.outstanding) > 0) {
    issues.push({ path: ['amount'], message: 'debts.errors.tooMuch' });
  }
  if (ctx.allowInterest && v.interest.trim() && !parsePositive(v.interest, ctx.currency)) {
    issues.push({ path: ['interest'], message: amountIssue(v.interest, ctx.currency) });
  }
  if (!v.accountId) issues.push({ path: ['accountId'], message: 'debts.errors.account' });
  if (ctx.internal && !v.counterpartAccountId) {
    issues.push({ path: ['counterpartAccountId'], message: 'debts.errors.theirAccount' });
  }
  if (!v.occurredOn) issues.push({ path: ['occurredOn'], message: 'add.errors.date' });
  if (ctx.askRate && !parseRateInput(v.rate)) {
    issues.push({ path: ['rate'], message: 'add.errors.rate' });
  }
  return issues;
}

export function toPaymentRequest(v: PaymentFormValues, ctx: PaymentContext): DebtPaymentRequest {
  const interest =
    ctx.allowInterest && v.interest.trim() ? parsePositive(v.interest, ctx.currency) : null;
  const request: DebtPaymentRequest = {
    accountId: v.accountId,
    ...(ctx.internal ? { counterpartAccountId: v.counterpartAccountId } : {}),
    amount: parsePositive(v.amount, ctx.currency)!,
    ...(interest ? { interest } : {}),
    occurredOn: v.occurredOn,
    ...(v.note.trim() ? { note: v.note.trim() } : {}),
    ...(ctx.askRate ? { rate: parseRateInput(v.rate)! } : {}),
    ...(v.paidCurrency && v.paidCurrency !== ctx.currency ? { paidCurrency: v.paidCurrency } : {}),
  };
  return debtPaymentRequestSchema.parse(request) as DebtPaymentRequest;
}

export function paymentFormSchema(ctx: PaymentContext) {
  return z
    .custom<PaymentFormValues>()
    .superRefine((v, c) => {
      for (const issue of checkPaymentForm(v, ctx)) {
        c.addIssue({ code: 'custom', path: issue.path, message: issue.message });
      }
    })
    .transform((v) => toPaymentRequest(v, ctx));
}
